/**
 * Serviço de Notícias Financeiras
 * 
 * Este serviço busca notícias de fontes públicas confiáveis usando RSS feeds.
 * Todas as fontes são gratuitas e públicas, respeitando direitos autorais.
 * 
 * Fontes utilizadas:
 * - InfoMoney (RSS público)
 * - Valor Econômico (RSS público)
 * - B3 Educacional (RSS público)
 * - Gov.br - Banco Central (RSS público)
 */

export interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  fullContent?: string;
  imageUrl: string;
  articleUrl: string;
  category: string;
  publishedDate: string;
  readTime?: string;
  source: string;
}

// RSS Feeds públicos de fontes confiáveis
const RSS_FEEDS = {
  infomoney: 'https://www.infomoney.com.br/feed/',
  valor: 'https://valor.globo.com/rss/',
  b3: 'https://www.b3.com.br/pt_br/noticias/rss/',
  bcb: 'https://www.bcb.gov.br/feeds/noticias'
};

/**
 * Parser RSS usando API pública gratuita (rss2json.com)
 * Alternativa: Podemos usar um proxy CORS se necessário
 */
const RSS_TO_JSON_API = 'https://api.rss2json.com/v1/api.json';

/**
 * Categoriza notícia baseada em palavras-chave do título/conteúdo
 */
function categorizeNews(title: string, content: string): string {
  const text = `${title} ${content}`.toLowerCase();
  
  if (text.match(/investimento|bolsa|ações|fundos|renda fixa|tesouro/i)) {
    return 'Investimentos';
  }
  if (text.match(/inflação|pib|economia|mercado|juros|selic/i)) {
    return 'Economia';
  }
  if (text.match(/dicas|planejamento|orçamento|poupar|economizar/i)) {
    return 'Finanças Pessoais';
  }
  
  return 'Notícias';
}

/**
 * Estima tempo de leitura baseado no comprimento do texto
 */
function estimateReadTime(content: string): string {
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / 200); // 200 palavras por minuto
  return `${minutes} min`;
}

/**
 * Extrai imagem do conteúdo HTML ou usa placeholder
 */
function extractImage(content: string, category: string): string {
  // Tenta extrair primeira imagem do conteúdo
  const imgMatch = content.match(/<img[^>]+src="([^">]+)"/);
  if (imgMatch && imgMatch[1]) {
    return imgMatch[1];
  }
  
  // Fallback para imagens do Unsplash por categoria
  const categoryImages: Record<string, string> = {
    'Investimentos': 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
    'Economia': 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80',
    'Finanças Pessoais': 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80',
    'Notícias': 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&q=80'
  };
  
  return categoryImages[category] || categoryImages['Notícias'];
}

/**
 * Remove tags HTML e limpa o conteúdo
 */
function stripHtml(html: string): string {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

/**
 * Busca notícias de um feed RSS específico
 */
async function fetchRSSFeed(feedUrl: string, source: string): Promise<NewsArticle[]> {
  try {
    const response = await fetch(
      `${RSS_TO_JSON_API}?rss_url=${encodeURIComponent(feedUrl)}&api_key=public&count=10`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch RSS: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.status !== 'ok') {
      throw new Error('RSS feed error');
    }
    
    return data.items.map((item: any, index: number) => {
      const cleanContent = stripHtml(item.description || item.content || '');
      const summary = cleanContent.substring(0, 300) + '...';
      const category = categorizeNews(item.title, cleanContent);
      
      return {
        id: `${source}-${index}-${Date.now()}`,
        title: item.title,
        summary,
        fullContent: item.content || item.description,
        imageUrl: item.thumbnail || item.enclosure?.link || extractImage(item.content || '', category),
        articleUrl: item.link,
        category,
        publishedDate: new Date(item.pubDate).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        readTime: estimateReadTime(cleanContent),
        source
      };
    });
  } catch (error) {
    console.error(`Error fetching RSS from ${source}:`, error);
    return [];
  }
}

/**
 * Busca notícias de todas as fontes configuradas
 */
export async function fetchAllNews(): Promise<NewsArticle[]> {
  try {
    const newsPromises = [
      fetchRSSFeed(RSS_FEEDS.infomoney, 'InfoMoney'),
      fetchRSSFeed(RSS_FEEDS.valor, 'Valor Econômico'),
      // Adicione mais feeds conforme necessário
    ];
    
    const newsArrays = await Promise.allSettled(newsPromises);
    
    const allNews = newsArrays
      .filter((result): result is PromiseFulfilledResult<NewsArticle[]> => result.status === 'fulfilled')
      .flatMap(result => result.value);
    
    // Ordena por data (mais recentes primeiro)
    return allNews.sort((a, b) => {
      const dateA = new Date(a.publishedDate);
      const dateB = new Date(b.publishedDate);
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Error fetching news:', error);
    return getFallbackNews();
  }
}

/**
 * Notícias de fallback caso a API falhe
 * Usando conteúdo público e educacional com URLs validadas
 */
export function getFallbackNews(): NewsArticle[] {
  return [
    {
      id: 'fallback-1',
      title: 'Guia Completo de Investimentos para Iniciantes',
      summary: 'Aprenda os conceitos fundamentais de investimentos, desde a renda fixa até a bolsa de valores, com explicações didáticas e exemplos práticos. Descubra como escolher ativos, avaliar riscos e construir uma carteira diversificada.',
      imageUrl: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
      articleUrl: 'https://www.b3.com.br/pt_br/educacao/',
      category: 'Investimentos',
      publishedDate: new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
      readTime: '10 min',
      source: 'B3 Educacional'
    },
    {
      id: 'fallback-2',
      title: 'Como Organizar Suas Finanças Pessoais',
      summary: 'Estratégias práticas para controlar gastos, criar um orçamento eficiente e alcançar seus objetivos financeiros de forma sustentável. Aprenda técnicas de organização, controle de despesas e planejamento familiar.',
      imageUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80',
      articleUrl: 'https://www.gov.br/pt-br/noticias/financas-impostos-e-gestao-publica',
      category: 'Finanças Pessoais',
      publishedDate: new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
      readTime: '8 min',
      source: 'Portal Gov.br'
    },
    {
      id: 'fallback-3',
      title: 'Entenda o Sistema Financeiro Brasileiro',
      summary: 'Descubra como funciona o sistema financeiro, o papel do Banco Central, e como essas instituições impactam sua vida financeira diária. Compreenda a estrutura do mercado e seus principais agentes.',
      imageUrl: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80',
      articleUrl: 'https://www.bcb.gov.br/estabilidadefinanceira/cidadaniafinanceira',
      category: 'Economia',
      publishedDate: new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
      readTime: '12 min',
      source: 'Banco Central do Brasil'
    },
    {
      id: 'fallback-4',
      title: 'Tesouro Direto: Segurança e Rentabilidade',
      summary: 'Conheça os títulos públicos disponíveis no Tesouro Direto, suas características, rentabilidades e qual é o mais adequado para seu perfil. Entenda Tesouro Selic, IPCA+ e Prefixado com exemplos práticos.',
      imageUrl: 'https://images.unsplash.com/photo-1560472355-536de3962603?w=800&q=80',
      articleUrl: 'https://www.tesourotransparente.gov.br/temas/divida-publica-federal/tesouro-direto',
      category: 'Investimentos',
      publishedDate: new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
      readTime: '15 min',
      source: 'Tesouro Nacional'
    },
    {
      id: 'fallback-5',
      title: 'Educação Financeira: Conceitos Essenciais',
      summary: 'Aprenda sobre juros compostos, inflação, diversificação de investimentos e outros conceitos fundamentais para sua saúde financeira. Desenvolva habilidades para tomar decisões financeiras mais inteligentes.',
      imageUrl: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80',
      articleUrl: 'https://www.bcb.gov.br/estabilidadefinanceira/educacaofinanceira',
      category: 'Finanças Pessoais',
      publishedDate: new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
      readTime: '10 min',
      source: 'Banco Central - Cidadania Financeira'
    },
    {
      id: 'fallback-6',
      title: 'Planejamento Financeiro de Longo Prazo',
      summary: 'Como criar metas financeiras realistas, poupar para a aposentadoria e construir um patrimônio sólido ao longo dos anos. Estratégias de investimento para diferentes horizontes de tempo e perfis de risco.',
      imageUrl: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&q=80',
      articleUrl: 'https://www.anbima.com.br/pt_br/educar/programas-educacionais.htm',
      category: 'Investimentos',
      publishedDate: new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
      readTime: '12 min',
      source: 'ANBIMA'
    }
  ];
}

/**
 * Cache de notícias com renovação diária automática
 * As notícias são atualizadas a cada 24 horas
 */
let newsCache: { data: NewsArticle[]; timestamp: number } | null = null;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas (renovação diária)

/**
 * Verifica se o cache expirou (passou de 1 dia)
 */
function isCacheExpired(): boolean {
  if (!newsCache) return true;
  
  const now = Date.now();
  const cacheAge = now - newsCache.timestamp;
  
  return cacheAge >= CACHE_DURATION;
}

/**
 * Atualiza notícias em background (sem bloquear UI)
 */
function refreshNewsInBackground(): void {
  fetchAllNews()
    .then(news => {
      if (news.length > 0) {
        newsCache = {
          data: news,
          timestamp: Date.now()
        };
        console.log('📰 Notícias atualizadas em background:', news.length, 'artigos');
      }
    })
    .catch(error => {
      console.error('Erro ao atualizar notícias em background:', error);
    });
}

/**
 * Agenda próxima atualização automática
 * Atualiza todos os dias às 6:00 AM
 */
function scheduleNextRefresh(): void {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(6, 0, 0, 0); // 6:00 AM do próximo dia
  
  const timeUntilRefresh = tomorrow.getTime() - now.getTime();
  
  setTimeout(() => {
    console.log('🔄 Iniciando atualização diária automática de notícias...');
    refreshNewsInBackground();
    scheduleNextRefresh(); // Agenda próxima atualização
  }, timeUntilRefresh);
  
  console.log(`⏰ Próxima atualização agendada para: ${tomorrow.toLocaleString('pt-BR')}`);
}

// Inicia agendamento automático quando o módulo é carregado
if (typeof window !== 'undefined') {
  scheduleNextRefresh();
}

export async function getNews(forceRefresh = false): Promise<NewsArticle[]> {
  // Se forçar refresh, busca imediatamente
  if (forceRefresh) {
    console.log('🔄 Refresh manual solicitado...');
    const news = await fetchAllNews();
    const finalNews = news.length > 0 ? news : getFallbackNews();
    
    newsCache = {
      data: finalNews,
      timestamp: Date.now()
    };
    
    return finalNews;
  }
  
  // Se cache está válido (< 24h), retorna do cache
  if (!isCacheExpired()) {
    const hoursOld = Math.floor((Date.now() - newsCache!.timestamp) / (60 * 60 * 1000));
    console.log(`✅ Usando cache de notícias (${hoursOld}h atrás)`);
    return newsCache!.data;
  }
  
  // Cache expirado - busca novas notícias
  console.log('🔄 Cache expirado, buscando notícias atualizadas...');
  const news = await fetchAllNews();
  
  // Se conseguiu buscar, atualiza cache
  if (news.length > 0) {
    newsCache = {
      data: news,
      timestamp: Date.now()
    };
    return news;
  }
  
  // Se falhou e tem cache antigo, usa cache antigo
  if (newsCache && newsCache.data.length > 0) {
    console.warn('⚠️ Falha ao buscar notícias, usando cache antigo');
    return newsCache.data;
  }
  
  // Último recurso: fallback
  console.log('📚 Usando notícias educacionais de fallback');
  const fallbackNews = getFallbackNews();
  newsCache = {
    data: fallbackNews,
    timestamp: Date.now()
  };
  
  return fallbackNews;
}
